import { TableBody, TableRow } from '@ksyos/design-system';
import type { EpisodeCategory } from '@common/enums';
import type {
    IOutclinicDashboardProcess,
    IStepGroup,
    OutclinicDashboardServiceCode,
} from '@common/interfaces/api/outclinicDashboardApi';
import { translateKey } from '@frontend/translate';
import type { ISwitch } from './OutclinicDashboardTableSwitches';
import { OutclinicDashboardTableCell } from './OutclinicDashboardTableCell';
import { OutclinicDashboardTableHeader } from './OutclinicDashboardTableHeader';
import { OutclinicDashboardTableNoResults } from './OutclinicDashboardTableNoResults';
import { OutclinicDashboardTableRowHeader } from './OutclinicDashboardTableRowHeader';
import { OutclinicDashboardTableWrapper } from './OutclinicDashboardTableWrapper';

interface IOutclinicDashboardTableProps {
    data: IOutclinicDashboardProcess[];
    episodeCategoryName?: EpisodeCategory;
    groups: IStepGroup[];
    hideSnoozed: boolean;
    isLoading: boolean;
    onHideSnoozedChange: () => void;
    onPaginationChange: (page: number, pageSize: number) => Promise<void>;
    onWarningsOnlyChange: () => void;
    page: number;
    pageSize: number;
    serviceCode: OutclinicDashboardServiceCode;
    setSnoozeStatus: (processId: string, isSnoozed: boolean, snoozedUntilDateTime?: string) => void;
    totalItems: number;
    warningsOnly: boolean;
}

export function OutclinicDashboardTable(props: IOutclinicDashboardTableProps) {
    const switches: ISwitch[] = [
        {
            isChecked: props.warningsOnly,
            key: 'warningsOnly',
            label: translateKey('outclinicDashboard.hideNonAlarms'),
            onChange: props.onWarningsOnlyChange,
        },
        {
            isChecked: props.hideSnoozed,
            key: 'hideSnoozed',
            label: translateKey('outclinicDashboard.hideSnoozed'),
            onChange: props.onHideSnoozedChange,
        },
    ];

    return (
        <OutclinicDashboardTableWrapper
            isLoading={props.isLoading}
            onPaginationChange={props.onPaginationChange}
            page={props.page}
            pageSize={props.pageSize}
            showPagination={props.data.length > 0 && !props.isLoading}
            showTableContainer={props.groups.length > 0}
            switches={switches}
            totalItems={props.totalItems}
        >
            <OutclinicDashboardTableHeader
                groups={props.groups}
                serviceCode={props.serviceCode}
            />
            <TableBody>
                {!props.isLoading &&
                    (props.data.length > 0 ? (
                        props.data.map((process, patientIndex) => (
                            <TableRow key={patientIndex}>
                                <OutclinicDashboardTableRowHeader
                                    patient={process.patient}
                                    processId={process.id}
                                    processNumber={process.processNumber}
                                    setSnoozeStatus={props.setSnoozeStatus}
                                    snoozedUntilDateTime={process.snoozedUntilDateTime}
                                    url={
                                        props.episodeCategoryName &&
                                        `/patients/${process.patient.id}/${props.episodeCategoryName}`
                                    }
                                />
                                {Object.values(process.steps).map(
                                    (step, stepIndex) =>
                                        step.status && (
                                            <OutclinicDashboardTableCell
                                                key={stepIndex}
                                                label={step.label}
                                                status={step.status}
                                            />
                                        )
                                )}
                            </TableRow>
                        ))
                    ) : (
                        <OutclinicDashboardTableNoResults />
                    ))}
            </TableBody>
        </OutclinicDashboardTableWrapper>
    );
}
